import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { vestResolver } from "@hookform/resolvers/vest";
import { create } from "vest";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { neutral } from "app/themes/colors";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import Dialog from "components/dialog";
import ErrorSnackbarContent from "components/error-snackbar-content";
import InputFormField from "components/input-form-field";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { CONTACT_DIALOG_PAPER_SX } from "features/calls/rolodex/contact-form-dialog.component";
import { useGetClinicQuery } from "services/clinics";
import { maxLength, required } from "utils/validations.utils";

import { displayNumber } from "../call-display.util";
import { seedFromCallerId } from "./caller-identity.util";

export const LEADS_NOT_SET_UP_MESSAGE =
  "Suno Leads isn't set up for your clinic yet. Finish Leads setup under Leads → Settings.";

export type SaveLeadFormValues = {
  first_name: string;
  last_name: string;
};

// What the host needs beyond the names: the comms location of the clinic the
// dialog resolved, so the lead files under it.
export type SaveLeadInput = SaveLeadFormValues & {
  locationId: string | null;
};

export type SaveLeadOutcome = { error: unknown } | null;

export const saveLeadValidator = create((data: SaveLeadFormValues) => {
  required("first_name", "First name", data.first_name.trim());
  maxLength("first_name", "First name", data.first_name, 255);
  required("last_name", "Last name", data.last_name.trim());
  maxLength("last_name", "Last name", data.last_name, 255);
});

type CommsError = { status?: unknown; data?: { detail?: unknown } };

// The comms service answers a user whose location has no lead settings with
// this exact 400; every other 4xx `detail` is written for the user to read.
export function saveLeadErrorMessage(error: unknown): string | null {
  const { status, data } = (error ?? {}) as CommsError;
  if (typeof status !== "number" || status < 400 || status >= 500) return null;
  const detail = data?.detail;
  if (typeof detail !== "string" || !detail.trim()) return null;
  if (/lead_setting/i.test(detail)) return LEADS_NOT_SET_UP_MESSAGE;
  return detail;
}

type SaveLeadDialogProps = {
  remoteNumber: string;
  fromName: string | null;
  // The clinic the call belongs to; null (a voicemail row, an older backend)
  // falls back to the clinic the user is working in.
  clinicId: number | null;
  onClose: () => void;
  // Resolves null on success (the dialog closes itself) or the error.
  onSave: (input: SaveLeadInput) => Promise<SaveLeadOutcome>;
  // Root styles (the softphone lifts its dialogs over the panel).
  sx?: SxProps;
};

// Files an unknown caller as a prospective patient in Suno Leads (ENG-2177).
// A Rolodex contact is an organization; a person calling about becoming a
// patient is a lead, so this is the third way to file a call, next to Quick
// add and Save to Rolodex. The number and clinic come from the call and are
// shown, not edited.
export default function SaveLeadDialog({
  remoteNumber,
  fromName,
  clinicId,
  onClose,
  onSave,
  sx
}: SaveLeadDialogProps) {
  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const resolvedClinicId = clinicId ?? currentClinic?.id ?? null;
  // The user's clinic list is a minimal projection without the comms
  // location, so the clinic is read in full (cached by RTK across opens).
  const { data: fetchedClinic, isFetching: isClinicFetching } =
    useGetClinicQuery(
      { clinicId: resolvedClinicId as number },
      { skip: resolvedClinicId === null }
    );
  // `skip` keeps the previous data, so gate on the id.
  const clinic =
    resolvedClinicId !== null && fetchedClinic?.id === resolvedClinicId
      ? fetchedClinic
      : null;
  const clinicName =
    clinic?.display_name ||
    clinic?.name ||
    (clinicId === null ? currentClinic?.name : undefined) ||
    null;

  const seed = seedFromCallerId(fromName);
  const form = useForm<SaveLeadFormValues>({
    mode: "onBlur",
    resolver: vestResolver(saveLeadValidator),
    defaultValues: { first_name: seed.first_name, last_name: seed.last_name }
  });

  const submit = form.handleSubmit(async (values) => {
    setIsSubmitting(true);
    setMessage(null);
    const outcome = await onSave({
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      locationId: clinic?.suno_comms_id || null
    });
    setIsSubmitting(false);
    if (!outcome) {
      onClose();
      return;
    }
    const text = saveLeadErrorMessage(outcome.error);
    if (text) {
      setMessage(text);
      return;
    }
    dispatch(setCustomMessage(<ErrorSnackbarContent response={outcome} />));
    dispatch(setOpen(true));
  });

  return (
    <Dialog
      title="Save caller as a lead"
      subtitle={`${displayNumber(remoteNumber)} will be the lead's phone number`}
      open
      onClose={onClose}
      // Sized like Save to Rolodex and the Rolodex dialogs (720 px).
      maxWidth="md"
      paperContentSx={CONTACT_DIALOG_PAPER_SX}
      sx={sx}
      actions={
        <>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={isClinicFetching}
            loading={isSubmitting}
            onClick={() => submit()}
            data-testid="save-lead-submit"
          >
            Save lead
          </LoadingButton>
        </>
      }
    >
      <FormProvider {...form}>
        <Stack
          component="form"
          spacing={2}
          sx={{ pb: 1 }}
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          data-testid="save-lead-form"
        >
          <Typography variant="body2" color="text.secondary">
            A lead is a prospective patient in Suno Leads. The call is logged on
            the lead, and you can text or email them from there.
          </Typography>
          <Stack direction="row" spacing={2}>
            <InputFormField
              name="first_name"
              label="First name"
              control={form.control}
              required
              autoFocus
              sx={{ m: 0 }}
              testId="save-lead-first-name"
            />
            <InputFormField
              name="last_name"
              label="Last name"
              control={form.control}
              required
              sx={{ m: 0 }}
              testId="save-lead-last-name"
            />
          </Stack>
          <Stack
            spacing={0.5}
            sx={{ px: 2, py: 1.5, borderRadius: 1, bgcolor: neutral[100] }}
            data-testid="save-lead-call-facts"
          >
            <Typography variant="body1">
              <Typography component="span" variant="body1" fontWeight={700}>
                Phone:{" "}
              </Typography>
              <span data-testid="save-lead-phone">
                {displayNumber(remoteNumber)}
              </span>
            </Typography>
            <Typography variant="body1">
              <Typography component="span" variant="body1" fontWeight={700}>
                Clinic:{" "}
              </Typography>
              <span data-testid="save-lead-clinic">
                {clinicName ?? (isClinicFetching ? "Loading…" : "Not set")}
              </span>
            </Typography>
          </Stack>
          {message && (
            <Alert severity="error" role="alert" data-testid="save-lead-error">
              {message}
            </Alert>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
